'use client'

import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { Mail, Phone, ArrowRight } from 'lucide-react'
import Shopmn from '@/assets/logo.svg'
import { useNavigation } from '@/lib/commerce/hooks'
import { formatImageURL } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'

const socialIcons: Record<string, (props: React.SVGProps<SVGSVGElement>) => React.JSX.Element> = {
  facebook: (props) => (
    <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
      <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
    </svg>
  ),
  instagram: (props) => (
    <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
      <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
    </svg>
  ),
  twitter: (props) => (
    <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
      <path d="M13.6823 10.6218L20.2391 3H18.6854L12.9921 9.61788L8.44486 3H3.2002L10.0765 13.0074L3.2002 21H4.75404L10.7663 14.0113L15.5685 21H20.8131L13.6819 10.6218H13.6823ZM11.5541 13.0956L10.8574 12.0991L5.31391 4.16971H7.70053L12.1742 10.5689L12.8709 11.5655L18.6861 19.8835H16.2995L11.5541 13.096V13.0956Z" />
    </svg>
  ),
  youtube: (props) => (
    <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
      <path fillRule="evenodd" d="M19.812 5.418c.861.23 1.538.907 1.768 1.768C21.998 8.746 22 12 22 12s0 3.255-.418 4.814a2.504 2.504 0 0 1-1.768 1.768c-1.56.419-7.814.419-7.814.419s-6.255 0-7.814-.419a2.505 2.505 0 0 1-1.768-1.768C2 15.255 2 12 2 12s0-3.255.417-4.814a2.507 2.507 0 0 1 1.768-1.768C5.744 5 11.998 5 11.998 5s6.255 0 7.814.418ZM15.194 12 10 15V9l5.194 3Z" clipRule="evenodd" />
    </svg>
  ),
}

function BrandRail({ brands, pathname }: { brands: any[], pathname: string }) {
  if (!brands || brands.length === 0) return null
  const items = brands.filter(b => b?.name)
  if (items.length === 0) return null
  const minPerHalf = Math.max(1, Math.ceil(8 / items.length))
  const base = Array.from({ length: minPerHalf }, () => items).flat()
  const row = [...base, ...base]
  return (
    <div className="bg-cream py-8 border-t border-foreground/10">
      <div className="mx-auto max-w-[1280px] px-6 lg:px-10 mb-6 flex items-end justify-between">
        <div className="text-[11px] tracking-[0.2em] uppercase font-semibold text-foreground/55">Манай түнш брэндүүд</div>
        <a href={navigateUrl('/brand', pathname)} className="inline-flex items-center gap-1.5 text-[14px] text-foreground hover:underline">
          Бүх брэнд <ArrowRight className="size-3.5" />
        </a>
      </div>
      <div className="relative overflow-hidden">
        <div className="flex gap-12 px-10 pill-marquee w-max shrink-0 items-center">
          {row.map((b, i) => (
            <a
              key={i}
              href={navigateUrl(`/product?brand=${b.id}`, pathname)}
              className="inline-flex items-center gap-2 lg:gap-3 text-foreground/70 hover:text-foreground font-bold font-gilroy whitespace-nowrap transition-colors"
              style={{ fontSize: 'clamp(28px,3vw,44px)', letterSpacing: '-0.02em' }}
            >
              {b.thumbnail ? (
                <img src={formatImageURL(b.thumbnail)} alt={b.name} className="size-8 lg:size-10 rounded-full object-cover bg-foreground/5 shrink-0" />
              ) : (
                <span className="size-8 lg:size-10 rounded-full bg-foreground/10 text-foreground/60 inline-flex items-center justify-center text-[14px] font-bold shrink-0">
                  {b.name.charAt(0)}
                </span>
              )}
              <span>{b.name}</span>
            </a>
          ))}
        </div>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-linear-to-r from-cream to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-linear-to-l from-cream to-transparent" />
      </div>
    </div>
  )
}

export default function Footer({ vendor, subscription, brands }: { vendor?: any, subscription?: any, brands?: any[] }) {
  const pathname = usePathname() || ''
  const { footer } = useNavigation({ vendor, subscription })
  const { info, help, socials, hasInfoSection } = footer
  const socialLinks = socials.map(s => ({ ...s, Icon: socialIcons[s.platform] }))
  const danVerified = !!vendor?.isDanVerified

  return (
    <footer className="bg-cream">
      <BrandRail brands={brands || []} pathname={pathname} />
      <div className="border-t border-foreground/10">
        <div className="mx-auto w-full max-w-[1280px] px-6 lg:px-10 pt-12 pb-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6">
            <div className="lg:col-span-3 flex flex-col items-center lg:items-start gap-6">
              <a href={navigateUrl('/status', pathname)} className="group flex flex-col items-center lg:items-start text-center lg:text-start gap-1 hover:opacity-80 transition-opacity">
                <span className="inline-flex items-center gap-2">
                  <span className={`size-2 rounded-full ${danVerified ? 'bg-emerald-500' : 'bg-red-500'}`} />
                  <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-foreground/70">
                    {danVerified ? 'Баталгаажсан' : 'Баталгаажаагүй'}
                  </span>
                </span>
                <span className="text-[12.5px] text-foreground/55 leading-snug">
                  {danVerified ? 'Улсын бүртгэлээр баталгаажсан' : 'Улсын бүртгэлээр баталгаажаагүй'}
                </span>
              </a>
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8 lg:gap-10">
              {hasInfoSection && (
                <div className="text-center sm:text-start">
                  <div className="text-[16px] font-semibold text-foreground mb-4">Мэдээлэл</div>
                  <ul className="space-y-2.5">
                    {info.map(item => (
                      <li key={item.name}>
                        <a href={navigateUrl(item.href, pathname)} className="text-[13.5px] text-foreground/65 hover:text-foreground transition-colors">
                          {item.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="text-center sm:text-start">
                <div className="text-[16px] font-semibold text-foreground mb-4">Үйлчилгээ</div>
                <ul className="space-y-2.5">
                  {help.map(item => (
                    <li key={item.name}>
                      <a href={navigateUrl(item.href, pathname)} className="text-[13.5px] text-foreground/65 hover:text-foreground transition-colors">
                        {item.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              {(vendor?.phone || vendor?.email) && (
                <div className="col-span-2 sm:col-span-1 text-center sm:text-start">
                  <div className="text-[16px] font-semibold text-foreground mb-4">Холбоо барих</div>
                  <ul className="space-y-2.5">
                    {vendor?.phone && (
                      <li>
                        <a href={`tel:${vendor.phone}`} className="inline-flex items-center justify-center sm:justify-start gap-2 text-[13.5px] text-foreground/65 hover:text-foreground transition-colors">
                          <Phone className="h-4 w-4" />
                          <span>{vendor.phone}</span>
                        </a>
                      </li>
                    )}
                    {vendor?.email && (
                      <li>
                        <a href={`mailto:${vendor.email}`} className="inline-flex items-center justify-center sm:justify-start gap-2 text-[13.5px] text-foreground/65 hover:text-foreground transition-colors max-w-full">
                          <Mail className="h-4 w-4 shrink-0" />
                          <span className="break-all">{vendor.email}</span>
                        </a>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
            {socialLinks.length > 0 && (
              <div className="lg:col-span-2 flex flex-col items-center lg:items-start">
                <div className="text-[16px] font-semibold text-foreground mb-4">Дагах</div>
                <div className="flex items-center gap-2">
                  {socialLinks.map((item) => (
                    <a key={item.name} href={item.href} target="_blank" rel="noopener noreferrer" aria-label={item.name} className="h-10 w-10 rounded-full bg-[#F1EEE7] hover:bg-[#E9E4DA] text-foreground grid place-items-center transition-colors">
                      <item.Icon aria-hidden="true" className="h-4 w-4" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="mx-auto w-full max-w-[1280px] px-6 lg:px-10 pt-2 pb-6 overflow-hidden">
          <div
            className="w-full font-bold font-gilroy text-foreground/15 leading-[0.85] tracking-[-0.06em] text-center select-none"
            style={{ fontSize: 'clamp(80px,18vw,260px)' }}
          >
            {vendor?.name || 'Shop.mn'}
          </div>
        </div>
        <div className="border-t border-foreground/10">
          <div className="mx-auto w-full max-w-[1280px] px-6 lg:px-10 py-5 flex flex-col md:flex-row items-center md:justify-between gap-4 text-[12px] text-foreground/55 md:pb-6 pb-24 lg:mb-0">
            <span className="text-center md:text-start">© {new Date().getFullYear()} {vendor?.name || 'Shop.mn'}. Бүх эрх хуулиар хамгаалагдсан.</span>
            <a href="https://shop.mn" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center md:justify-start gap-1.5 hover:text-foreground transition-colors">
              <span>Онлайн худалдааны шийдэл</span>
              <Image src={Shopmn} alt="Shop.mn" className="h-4 w-auto" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}
